import { useState, useEffect } from "react";
import { LuCheck, LuPencilLine, LuX } from "react-icons/lu";
import { CountryCodeSelect, usePhoneParts } from "./PhoneInput";
import { formatPhone, phoneError } from "../utils/phone";

const inputClasses =
  "h-9 flex-1 rounded-md border border-slate-300 bg-white px-3 text-[13px] text-slate-900 outline-none focus:border-[#5B7CFF] focus:ring-2 focus:ring-[#5B7CFF]/20";

// Country code picker + number, styled like the plain FormField input
function PhoneDraftInput({ value, onChange, onKeyDown }) {
  const { dial, number, changeDial, changeNumber } = usePhoneParts(value, onChange);
  return (
    <div className="flex h-9 flex-1 overflow-hidden rounded-md border border-slate-300 bg-white focus-within:border-[#5B7CFF] focus-within:ring-2 focus-within:ring-[#5B7CFF]/20">
      <CountryCodeSelect
        value={dial}
        onChange={changeDial}
        className="border-r border-slate-200 bg-slate-50 px-2 text-[13px] text-slate-700"
      />
      <input
        autoFocus
        type="tel"
        inputMode="numeric"
        value={number}
        onChange={(e) => changeNumber(e.target.value)}
        onKeyDown={onKeyDown}
        className="min-w-0 flex-1 bg-transparent px-3 text-[13px] text-slate-900 outline-none"
      />
    </div>
  );
}

export default function FormField({ label, value, required, placeholder = "Click to add", onCommit, phone = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [error, setError] = useState("");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const commit = () => {
    const draftError = phone ? phoneError(draft) : "";
    if (draftError) {
      setError(draftError);
      return;
    }
    setError("");
    setEditing(false);
    if (draft !== value) onCommit?.(draft);
  };

  const cancel = () => {
    setDraft(value || "");
    setError("");
    setEditing(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") commit();
    if (e.key === "Escape") cancel();
  };

  const displayValue = phone ? formatPhone(value) : value;

  return (
    <div className="space-y-1.5">
      <label className="flex items-center text-[13px] font-medium text-slate-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>

      {editing ? (
        <>
          <div className="flex items-center gap-2">
            {phone ? (
              <PhoneDraftInput
                value={draft}
                onChange={(next) => {
                  setDraft(next);
                  setError("");
                }}
                onKeyDown={onKeyDown}
              />
            ) : (
              <input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                className={inputClasses}
              />
            )}
            <button onClick={commit} className="rounded-md border border-emerald-300 p-1.5 text-emerald-600 hover:bg-emerald-50">
              <LuCheck size={15} />
            </button>
            <button onClick={cancel} className="rounded-md border border-red-300 p-1.5 text-red-500 hover:bg-red-50">
              <LuX size={15} />
            </button>
          </div>
          {error && <p className="text-[12px] text-red-500">{error}</p>}
        </>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="group flex h-9 w-full items-center justify-between rounded-md border border-transparent px-3 text-left text-[13px] text-slate-900 hover:border-slate-200 hover:bg-slate-50"
        >
          <span className={displayValue ? "" : "text-slate-400"}>{displayValue || placeholder}</span>
          <LuPencilLine size={14} className="text-slate-400 opacity-0 transition-opacity group-hover:opacity-100" />
        </button>
      )}
    </div>
  );
}
